import { Checkmark } from "./Checkmark";
import { SICK_DAY } from "../data/sickDay";
import { T } from "../styles/theme";
import type { CheckedMap } from "../types";

interface SickDayViewProps {
  checkedSick: CheckedMap;
  setCheckedSick: (next: (prev: CheckedMap) => CheckedMap) => void;
}

/** The "Sick Day Plan" tab — three energy tiers of bare-minimum tasks. */
export function SickDayView({ checkedSick, setCheckedSick }: SickDayViewProps) {
  return (
    <div>
      <div
        style={{
          background: `linear-gradient(135deg,#fff5f3,${T.white})`,
          borderRadius: 20,
          padding: "18px 18px 14px",
          marginBottom: 14,
          border: `1.5px solid ${T.blush}66`,
          boxShadow: `0 4px 20px ${T.blush}22`,
        }}
      >
        <div style={{ fontSize: 26, marginBottom: 4 }}>🤒</div>
        <h2 style={{ margin: 0, fontSize: 20, color: "#8b3a2a", fontFamily: T.display, fontWeight: 700 }}>
          Sick Day Plan
        </h2>
        <p style={{ margin: "5px 0 0", fontSize: 12, color: T.inkMid, fontStyle: "italic", lineHeight: 1.5 }}>
          {SICK_DAY.description}
        </p>
        <div
          style={{
            marginTop: 10,
            background: "#fff0ee",
            borderRadius: 10,
            padding: "9px 12px",
            fontSize: 11,
            color: "#8b4a3a",
            lineHeight: 1.5,
            border: `1px solid ${T.blush}44`,
          }}
        >
          💛 Pick the tier that matches today. Any hard day done with grace is never a failure.
        </div>
      </div>

      {SICK_DAY.tiers.map((tier) => {
        const tDone = tier.tasks.filter((t) => checkedSick[t.id]).length;
        return (
          <div
            key={tier.tier}
            style={{
              background: T.white,
              borderRadius: 16,
              marginBottom: 10,
              overflow: "hidden",
              boxShadow: "0 2px 12px rgba(45,32,22,0.06)",
              border: `1px solid ${tier.accent}33`,
            }}
          >
            <div
              style={{
                padding: "10px 16px",
                background: tier.color,
                borderBottom: `1px solid ${tier.accent}22`,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: 12, fontWeight: 700, color: tier.accent, fontFamily: T.display }}>
                {tier.tier}
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: tier.accent,
                  background: "rgba(255,255,255,0.7)",
                  borderRadius: 99,
                  padding: "2px 8px",
                }}
              >
                {tDone}/{tier.tasks.length}
              </span>
            </div>
            {tier.tasks.map((task, i) => {
              const isDone = !!checkedSick[task.id];
              return (
                <div
                  key={task.id}
                  onClick={() => setCheckedSick((p) => ({ ...p, [task.id]: !p[task.id] }))}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    padding: "12px 16px",
                    gap: 11,
                    cursor: "pointer",
                    borderBottom: i < tier.tasks.length - 1 ? `1px solid ${tier.color}` : "none",
                    background: isDone ? "#f8fdf8" : T.white,
                    transition: "background 0.2s",
                    borderLeft: `3px solid ${isDone ? T.sage : tier.accent}`,
                  }}
                >
                  <div
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: "50%",
                      flexShrink: 0,
                      marginTop: 1,
                      border: isDone ? "none" : `2px solid ${tier.accent}`,
                      background: isDone ? T.sage : "transparent",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      transition: "all 0.2s",
                    }}
                  >
                    {isDone && <Checkmark />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div
                      style={{
                        fontSize: 13,
                        color: isDone ? T.sage : T.ink,
                        textDecoration: isDone ? "line-through" : "none",
                        lineHeight: 1.45,
                      }}
                    >
                      {task.label}
                    </div>
                    <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                      <span
                        style={{
                          fontSize: 9,
                          color: tier.accent,
                          background: tier.color,
                          borderRadius: 6,
                          padding: "2px 7px",
                          fontWeight: 600,
                        }}
                      >
                        🕐 {task.time}
                      </span>
                      <span
                        style={{
                          fontSize: 9,
                          color: T.inkLight,
                          background: T.parchment,
                          borderRadius: 6,
                          padding: "2px 7px",
                          fontStyle: "italic",
                        }}
                      >
                        ⏱ {task.duration}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
